// 이터널 리턴 데미지 계산 (출처: https://eternalreturn.fandom.com/wiki/Damage)
// 메타(choco-system /api/bser/damage-calculator/meta)의 원본 필드명(camelCase)을 그대로 사용한다.
// 비율 값은 BSER 원본과 같이 소수(0.2 = 20%). 예외로 모드 보정과 쿨다운 감소는 % 정수라 100으로 나눈다.

export const SLOTS = ['Weapon', 'Chest', 'Head', 'Arm', 'Leg'];

// 스킬에 딸린 아이템(획득할 수 없고 스탯도 없음). 메타에는 게임 아이템 테이블 그대로 들어와 있어 계산기 선택지에서는 뺀다.
// - 에키온 '카드모스의 부름 Lv2/Lv3', 프리야 '자연의 응답 Lv2/Lv3'
const SKILL_BOUND_ITEM_PREFIXES = ['카드모스의 부름', '자연의 응답'];

export function excludeSkillBoundItems(items) {
  return items.filter((item) => !SKILL_BOUND_ITEM_PREFIXES.some((prefix) => item.name.startsWith(prefix)));
}

export const MODES = [
  { key: 'squad', label: '스쿼드' },
  { key: 'duo', label: '듀오' },
  { key: 'solo', label: '솔로' },
  { key: 'cobalt', label: '코발트' },
];

export const BASE_CRIT_DAMAGE = 0.65;
// 적응형 능력치: 공격력/스킬 증폭 중 현재 더 높은 쪽으로 자동 전환, 공격력 1:1 / 스킬 증폭 1:2
// (출처: https://eternalreturn.fandom.com/wiki/Statistics#Adaptability)
export const SKILL_AMP_PER_ADAPTIVE = 2;
export const ATTACK_POWER_PER_ADAPTIVE = 1;

// 아이템 스탯 키 → 레벨 비례 키
const BY_LEVEL_KEYS = {
  attackPower: 'attackPowerByLv',
  defense: 'defenseByLv',
  skillAmp: 'skillAmpByLevel',
  skillAmpRatio: 'skillAmpRatioByLevel',
  adaptiveForce: 'adaptiveForceByLevel',
  maxHp: 'maxHpByLv',
  attackSpeedRatio: 'attackSpeedRatioByLv',
  increaseBasicAttackDamage: 'increaseBasicAttackDamageByLv',
  increaseBasicAttackDamageRatio: 'increaseBasicAttackDamageRatioByLv',
  preventBasicAttackDamaged: 'preventBasicAttackDamagedByLv',
  preventBasicAttackDamagedRatio: 'preventBasicAttackDamagedRatioByLv',
  preventSkillDamaged: 'preventSkillDamagedByLv',
  preventSkillDamagedRatio: 'preventSkillDamagedRatioByLv',
};

// 숙련도 option 이름(PascalCase) → 스탯 키
const MASTERY_OPTION_KEYS = {
  AttackPower: 'attackPower',
  AttackSpeedRatio: 'attackSpeedRatio',
  SkillAmpRatio: 'skillAmpRatio',
  IncreaseBasicAttackDamageRatio: 'increaseBasicAttackDamageRatio',
  PreventBasicAttackDamagedRatio: 'preventBasicAttackDamagedRatio',
  PreventSkillDamagedRatio: 'preventSkillDamagedRatio',
};

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * 캐릭터/레벨/무기/숙련도/아이템으로 최종 스탯을 계산한다.
 * @param {object} p
 * @param {object} p.character 메타 characters[] 원소
 * @param {number} p.level 캐릭터 레벨 (1~20)
 * @param {string} p.weaponType 무기 타입 (예: 'TwoHandSword')
 * @param {number} p.masteryLevel 무기 숙련도 레벨
 * @param {object[]} p.items 장착 아이템 (메타 items[] 원소), 빈 칸은 null
 * @param {'auto'|'attack'|'skill'} p.adaptiveTo 적응형 능력치 변환 대상 (auto: 적응형 제외 공격력/스킬 증폭 중 높은 쪽)
 */
export function computeStats({ character, level, weaponType, masteryLevel, items, adaptiveTo }) {
  const base = character?.base_stats || {};
  const up = character?.level_up_stats || {};
  const lvBonus = Math.max(0, n(level) - 1);
  const stat = (key) => n(base[key]) + n(up[key]) * lvBonus;

  const itemSum = (key) => (items || []).reduce((sum, item) => {
    if (!item) return sum;
    const s = item.stats || {};
    const byLvKey = BY_LEVEL_KEYS[key];
    return sum + n(s[key]) + (byLvKey ? n(s[byLvKey]) * n(level) : 0);
  }, 0);

  const mastery = {};
  for (const opt of character?.mastery_stats?.[weaponType] || []) {
    const key = MASTERY_OPTION_KEYS[opt.option];
    if (key) mastery[key] = (mastery[key] || 0) + n(opt.value_per_level) * n(masteryLevel);
  }
  const m = (key) => mastery[key] || 0;

  const skillAmpRatio = n(base.skillAmpRatio) + itemSum('skillAmpRatio') + m('skillAmpRatio');
  const attackWithoutAdaptive = stat('attackPower') + itemSum('attackPower') + m('attackPower');
  const skillAmpFlatWithoutAdaptive = stat('skillAmp') + itemSum('skillAmp');

  const adaptiveForce = stat('adaptiveForce') + itemSum('adaptiveForce');
  const adaptiveAppliedTo = adaptiveTo === 'attack' || adaptiveTo === 'skill'
    ? adaptiveTo
    : (skillAmpFlatWithoutAdaptive * (1 + skillAmpRatio) > attackWithoutAdaptive ? 'skill' : 'attack');
  const adaptiveAttack = adaptiveAppliedTo === 'attack' ? adaptiveForce * ATTACK_POWER_PER_ADAPTIVE : 0;
  const adaptiveSkill = adaptiveAppliedTo === 'skill' ? adaptiveForce * SKILL_AMP_PER_ADAPTIVE : 0;

  return {
    maxHp: stat('maxHp') + itemSum('maxHp'),
    hpRegen: stat('hpRegen') + itemSum('hpRegen'),
    attackPower: attackWithoutAdaptive + adaptiveAttack,
    defense: stat('defense') + itemSum('defense'),
    skillAmp: (skillAmpFlatWithoutAdaptive + adaptiveSkill) * (1 + skillAmpRatio),
    adaptiveAppliedTo,
    skillAmpRatio,
    adaptiveForce,
    criticalStrikeChance: Math.min(1, n(base.criticalStrikeChance) + itemSum('criticalStrikeChance')),
    criticalStrikeDamage: itemSum('criticalStrikeDamage'),
    preventCriticalStrikeDamaged: itemSum('preventCriticalStrikeDamaged'),
    penetrationDefense: itemSum('penetrationDefense') + itemSum('uniquePenetrationDefense'),
    penetrationDefenseRatio: Math.min(1, itemSum('penetrationDefenseRatio') + itemSum('uniquePenetrationDefenseRatio')),
    increaseBasicAttackDamage: itemSum('increaseBasicAttackDamage'),
    increaseBasicAttackDamageRatio: n(base.increaseBasicAttackDamageRatio)
      + itemSum('increaseBasicAttackDamageRatio') + m('increaseBasicAttackDamageRatio'),
    preventBasicAttackDamaged: itemSum('preventBasicAttackDamaged'),
    preventBasicAttackDamagedRatio: n(base.preventBasicAttackDamagedRatio)
      + itemSum('preventBasicAttackDamagedRatio') + m('preventBasicAttackDamagedRatio'),
    preventSkillDamaged: itemSum('preventSkillDamaged'),
    preventSkillDamagedRatio: n(base.preventSkillDamagedRatio)
      + itemSum('preventSkillDamagedRatio') + m('preventSkillDamagedRatio'),
    attackSpeedRatio: itemSum('attackSpeedRatio') + m('attackSpeedRatio'),
    lifeSteal: itemSum('lifeSteal'),
    cooldownReduction: itemSum('cooldownReduction') / 100, // BSER 원본이 % 정수(예: 15)
  };
}

/** 모드 보정(%) 조회. 무기별 값이 없으면 무기 무관 공통("None") 값을 쓴다. */
export function modeModifier(character, weaponType, mode) {
  const mods = character?.mode_modifiers || {};
  const row = mods[weaponType] || mods.None || {};
  return {
    dealt: n(row[`${mode}IncreaseModeDamageRatio`]) / 100,
    taken: n(row[`${mode}PreventModeDamageRatio`]) / 100,
  };
}

/** 관통 적용 후 유효 방어력 (% 관통 먼저, 고정 관통 나중, 최소 0). */
export function effectiveDefense(defense, penetrationRatio, penetrationFlat) {
  return Math.max(0, n(defense) * (1 - n(penetrationRatio)) - n(penetrationFlat));
}

/** 방어력에 의한 피해 배율: 100 / (100 + 방어력) */
export function defenseMultiplier(defense) {
  return 100 / (100 + Math.max(0, n(defense)));
}

/**
 * 기본 공격 피해.
 * @param {object} attacker computeStats 결과
 * @param {object} target computeStats 결과 (또는 같은 필드를 가진 피벗)
 * @param {{dealt:number, taken:number}} attackerMode 공격자 모드 보정
 * @param {{dealt:number, taken:number}} targetMode 대상 모드 보정
 */
export function basicAttackDamage(attacker, target, attackerMode = { dealt: 0 }, targetMode = { taken: 0 }) {
  const effDef = effectiveDefense(target.defense, attacker.penetrationDefenseRatio, attacker.penetrationDefense);
  const defMul = defenseMultiplier(effDef);
  const ampMul = Math.max(0, 1 + n(attacker.increaseBasicAttackDamageRatio) - n(target.preventBasicAttackDamagedRatio));
  const critMul = 1 + Math.max(0,
    BASE_CRIT_DAMAGE + n(attacker.criticalStrikeDamage) - n(target.preventCriticalStrikeDamaged));
  const modeMul = (1 + n(attackerMode.dealt)) * (1 + n(targetMode.taken));

  const hit = (crit) => Math.max(0,
    (n(attacker.attackPower) * defMul * (crit ? critMul : 1) * ampMul
      + n(attacker.increaseBasicAttackDamage) - n(target.preventBasicAttackDamaged)) * modeMul);

  const normal = hit(false);
  const critical = hit(true);
  const chance = Math.min(1, Math.max(0, n(attacker.criticalStrikeChance)));
  return {
    effectiveDefense: effDef,
    defenseReduction: 1 - defMul,
    normal,
    critical,
    expected: normal * (1 - chance) + critical * chance,
    hitsToKill: n(target.maxHp) > 0 ? Math.ceil(n(target.maxHp) / Math.max(1e-9, normal * (1 - chance) + critical * chance)) : null,
  };
}

/** 방어 지표: 방어력 피해 감소율, 유효 체력(기본 공격 기준, 관통 없음). */
export function defenseSummary(stats) {
  const reduction = 1 - defenseMultiplier(stats.defense);
  return {
    defenseReduction: reduction,
    effectiveHp: n(stats.maxHp) / defenseMultiplier(stats.defense),
  };
}

/** 단일 캐릭터 모드의 기본 대상(피벗). 화면에서 편집 가능. */
export const DEFAULT_PIVOT = {
  maxHp: 2500,
  defense: 120,
  preventBasicAttackDamagedRatio: 0,
  preventBasicAttackDamaged: 0,
  preventSkillDamagedRatio: 0,
  preventCriticalStrikeDamaged: 0,
};
